import { encode as base64encode } from "base64-arraybuffer";
import crypto from "crypto";

async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  const base64Digest = base64encode(digest);
  return base64Digest.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * This creates the most "secure" string.
 * The passed in size needs to be cut in half, because it generates twice bigger hex
 * @param size
 * @returns {string}
 */
function randomstring(size) {
  return crypto.randomBytes(size / 2).toString("hex");
}

const state = {
  selectedMspUser: null,
  selectedUser: null,
  token: null,
  role: null,
  authUser: null,
  mspId: null,
  backupCodes: null,
  setIsDefault: null,
};

const getters = {
  selectedUser: (state) => state.selectedUser,
  selectedMspUser: (state) => state.selectedMspUser,
  token: (state) => state.token,
  role: (state) => state.role,
  authUser: (state) => state.authUser,
  mspId: (state) => state.mspId,
  backupCodes: (state) => state.backupCodes,
  isDefault: (state) => state.isDefault,
};

const actions = {
  /**
   * Either the user id or the email address must be provided.
   * If the oauth keys have already been created, they are returned without any further changes.
   * @values user: { id: Number, email: String }
   * @returns userObject
   */
  async registerCustomerForOAuth({ commit }, user) {
    const response = await this._vm.$http.post(
      "/restapi/auth/adminregister/",
      user
    );

    commit("registerCustomer", response.data);
    return response.data;
  },

  async setToken({ commit }, token) {
    commit("setSelectedCustomer", token);
  },

  async setUser({ commit }, selectedUser) {
    commit("setUser", selectedUser);
  },

  async setMspUser({ commit }, selectedMspUser) {
    commit("setMspUser", selectedMspUser);
  },

  async setMspId({ commit }, mspId) {
    commit("setMspId", mspId);
  },

  async setRole({ commit }, role) {
    commit("setRole", role);
  },

  async enable2fa({ commit }, code) {
    const response = await this._vm.$http.post(
      "/restapi/auth/two-factor-auth",
      code
    );

    this._vm.$userPrefs.updateUserPrefs({
      twoFactorAuthEnabled: response.data.created,
    });

    commit("set2Fa", response.data.created);
    commit("setBackupCodes", response.data.backupCodes);
  },

  async disable2fa({ commit }) {
    const response = await this._vm.$http.delete(
      "/restapi/auth/two-factor-auth"
    );

    this._vm.$userPrefs.updateUserPrefs({
      twoFactorAuthEnabled: !response.data.disabled,
    });

    commit("set2Fa", !response.data.disabled);
    commit("setBackupCodes", null);
  },

  async getAuthUser({ commit }) {
    if (this._vm.$userPrefs.getCurrentToken()) {
      const payload = atob(this._vm.$userPrefs.getCurrentToken().split(".")[1]);
      const jwtPayload = JSON.parse(payload);
      commit("setRole", jwtPayload.vue_role);
      commit("setAuthUser", {
        id: +jwtPayload.sub,
        accountname: jwtPayload.name,
        email: jwtPayload.email,
        mspAccountName: jwtPayload.account_name,
        twoFactorAuthEnabled: jwtPayload["2fa_enabled"],
      });
      if (jwtPayload.vue_role === 1) {
        commit("setMspId", this._vm.$userPrefs.getSelectedMSP());
      }
      if (jwtPayload.vue_role === 3) {
        commit("setMspId", jwtPayload.msp_id);
        commit("setIsDefault", jwtPayload.is_default);
      }
      if (jwtPayload.vue_role === 4) {
        const selectUser = {
          id: jwtPayload.user_id,
          accountName: jwtPayload.account_name,
          mspAccountName: jwtPayload.account_name,
          allCustomers: false,
        };
        commit("setUser", selectUser);
      }
    }
  },

  async requestToken({ commit }, user) {
    const code_verifier = randomstring(128);
    const state = randomstring(40);
    const code_challenge = await generateCodeChallenge(code_verifier);
    const client_id = "2";
    const code_challenge_method = "S256";

    let params = {
      state,
      code_verifier,
      code_challenge,
      redirect_uri: process.env.VUE_APP_API_URL + "/restapi/oauth/callback",
      client_id,
      code_challenge_method,
      response_type: "code",
      secret: user.secret,
      code: user.code,
    };
    if (user["code"] != null) {
      params = { ...params, code: user["code"] };
    }
    const auth = {
      username: user["username"],
      password: user["password"],
    };

    let response = await this._vm.$http.get("restapi/oauth/authorize", {
      params: params,
      auth,
    });
    this._vm.$http.setAuthorisationToken(response.data.access_token);
    commit("setToken", response.data);
    const tokens = response.data.access_token.split(".");

    const backupCodes = response.data.backupCodes || null;
    const payload = atob(tokens[1]);
    const jwtPayload = JSON.parse(payload);
    const vue_role = jwtPayload["vue_role"];
    const twoFactorAuthEnabled = jwtPayload["2fa_enabled"] || false;
    commit("setRole", vue_role);
    commit("setBackupCodes", backupCodes);

    commit("setAuthUser", {
      id: jwtPayload["sub"],
      accountname: jwtPayload["name"],
      email: jwtPayload["email"],
      twoFactorAuthEnabled: twoFactorAuthEnabled,
    });

    if (vue_role === 3) {
      commit("setMspId", jwtPayload["msp_id"]);
    }

    if (vue_role === 4) {
      const selectUser = {
        id: jwtPayload["user_id"],
        accountName: jwtPayload["account_name"],
        allCustomers: false,
      };
      commit("setUser", selectUser);
    }
    return response;
  },

  // eslint-disable-next-line no-unused-vars
  async forgotPassword({ commit }, email) {
    const response = await this._vm.$http.post(
      "/restapi/auth/password/forgot",
      email
    );
    return response.data;
  },
};

const mutations = {
  setUser: (state, selectedUser) =>
    (state.selectedUser = {
      accountname: selectedUser.accountname,
      id: selectedUser.id,
      allCustomers: selectedUser.allCustomers,
      uuid: selectedUser.uuid_str,
      timezone: selectedUser.timezone,
    }),
  setToken: (state, token) => (state.token = token),
  setRole: (state, role) => (state.role = role),
  set2Fa: (state, enabled) => (state.authUser.twoFactorAuthEnabled = enabled),
  setBackupCodes: (state, codes) => (state.backupCodes = codes),
  setMspId: (state, mspId) => (state.mspId = mspId),
  setAuthUser: (state, authUser) => (state.authUser = authUser),
  setMspUser: (state, selectedMspUser) =>
    (state.selectedMspUser = selectedMspUser),
  setIsDefault: (state, isDefault) => (state.isDefault = isDefault),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
